/**
 * Where the floating Otis launcher is allowed to appear.
 *
 * ## Why this is a whitelist
 *
 * This used to live inside OtisWidget as a list of routes to HIDE on, and a
 * blacklist of public routes goes stale the moment somebody adds one. That is
 * exactly what happened: `/share/cable/:tokenId` shipped, the hide-list had
 * never heard of it, and an electrician holding a read-only link got a
 * floating chat button offering "anything about {company}'s history — jobs,
 * customers, old notes, the lot".
 *
 * So it is inverted. This lists the top-level segments that sit inside
 * `<AuthGuard>` in App.tsx, and everything else — every public route that
 * exists now and every one added later — gets nothing.
 *
 * The two ways to get it wrong are not equally bad:
 *
 *   - Forget to add a new AUTHENTICATED area → Otis is missing from a screen
 *     you are signed into. You will see it within a minute, and it leaks
 *     nothing.
 *   - Forget to add a new PUBLIC route to a hide-list → a stranger outside the
 *     company gets a chat window onto the company's memory.
 *
 * Only the first is a fair thing to get wrong, so the code is arranged so that
 * is the one that happens. `otisRoutes.test.ts` reads App.tsx and fails if a
 * route declared outside `<AuthGuard>` would show the widget, so the next
 * public route is caught by CI rather than by the person it leaks to.
 */

/**
 * Top-level path segments served inside `<AuthGuard>` in App.tsx.
 *
 * Keep in step with App.tsx. The guard test will tell you if a public route
 * drifts in here; nothing can tell you if an authenticated one is missing, so
 * add the segment when you add the route.
 */
export const AUTHENTICATED_ROUTE_SEGMENTS: readonly string[] = [
  'admin',
  // The Lite shell — one signed-in surface for 1-3 person tenants.
  'app',
  'admin-seed',
  'customers',
  'dashboard',
  'deals',
  'faults',
  'home',
  'operations',
  'ops',
  'ops-legacy',
  'pipeline',
  'pipeline-settings',
  'platform',
  'playground',
  'projects',
  'provisioning',
  'quote-review',
  'reports',
  'sales',
  'sales-legacy',
  'schedule',
  'services',
  'settings',
  'tech',
];

/**
 * Authenticated screens that still don't want the floating launcher — right
 * now just the full Otis page, where floating a second Otis over the first is
 * only ever in the way.
 */
const SUPPRESSED_EXACT_PATHS: readonly string[] = ['/home/ask'];

/** The first path segment, with no leading or trailing slashes. */
function firstSegment(pathname: string): string {
  return pathname.split('?')[0].split('#')[0].split('/').filter(Boolean)[0] ?? '';
}

/**
 * Is this pathname an authenticated app screen that should carry Otis?
 *
 * Public / unknown routes answer `false`, which is the safe direction.
 */
export function isOtisRoute(pathname: string): boolean {
  if (SUPPRESSED_EXACT_PATHS.includes(pathname)) return false;
  const seg = firstSegment(pathname);
  if (!seg) return false; // '/' is the landing page
  return AUTHENTICATED_ROUTE_SEGMENTS.includes(seg);
}
